import { useEffect, useState } from "react";
import { ClipboardText, CheckSquare, FileText, Plus, Trash, CheckCircle, Warning, XCircle, PaperPlaneRight, X, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../../lib/api";
import { HabitRow } from "../client/ClientHabits";
import BadgeWall from "../../components/BadgeWall";

export default function TrainerCoaching() {
  const [tab, setTab] = useState("checkins");
  return (
    <div className="space-y-8" data-testid="trainer-coaching-page">
      <div>
        <div className="tracker text-brand mb-3">[ Client accountability ]</div>
        <h1 className="font-display text-4xl sm:text-5xl">COACHING.</h1>
        <p className="text-zinc-400 mt-3">Weekly check-ins, daily habits, and onboarding forms — all in one place.</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {[
          { id: "checkins", label: "Check-ins", icon: <ClipboardText size={14} weight="bold" /> },
          { id: "habits", label: "Habits", icon: <CheckSquare size={14} weight="bold" /> },
          { id: "forms", label: "Forms", icon: <FileText size={14} weight="bold" /> },
        ].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-5 py-2.5 tracker flex items-center gap-2 border ${tab === t.id ? "bg-brand border-brand text-white" : "border-white/10 text-zinc-400 hover:text-white"}`}
            data-testid={`coaching-tab-${t.id}`}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {tab === "checkins" && <CheckinsTab />}
      {tab === "habits" && <HabitsTab />}
      {tab === "forms" && <FormsTab />}
    </div>
  );
}

function CheckinsTab() {
  const [filter, setFilter] = useState("submitted");
  const [items, setItems] = useState(null);
  const [comments, setComments] = useState({});
  const [drafting, setDrafting] = useState({});
  const [lightbox, setLightbox] = useState(null);

  const load = async () => {
    try {
      const params = filter === "all" ? {} : { status: filter };
      const { data } = await api.get("/checkins", { params });
      setItems(data);
    } catch (_e) { setItems([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const draftWithAI = async (id) => {
    setDrafting((s) => ({ ...s, [id]: true }));
    try {
      const { data } = await api.post(`/checkins/${id}/draft-reply`);
      setComments((s) => ({ ...s, [id]: data.draft }));
      toast.success("Draft ready — tweak it, then send");
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI draft failed");
    } finally {
      setDrafting((s) => ({ ...s, [id]: false }));
    }
  };

  const review = async (id) => {
    const comment = (comments[id] || "").trim();
    if (!comment) { toast.error("Write a comment first"); return; }
    try {
      await api.post(`/checkins/${id}/review`, { comment });
      toast.success("Review sent to client");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Review failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="checkins-tab">
      <div className="flex gap-2">
        {[["submitted", "Awaiting review"], ["reviewed", "Reviewed"], ["all", "All"]].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)}
            className={`px-4 py-1.5 tracker text-[10px] border ${filter === v ? "border-brand text-brand-light" : "border-white/10 text-zinc-500"}`}
            data-testid={`checkin-filter-${v}`}>{l}</button>
        ))}
      </div>
      {items === null ? (
        <div className="tracker text-zinc-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="card-flat p-10 text-center text-zinc-500 tracker" data-testid="checkins-empty">
          {filter === "submitted" ? "Inbox zero — no check-ins waiting" : "Nothing here yet"}
        </div>
      ) : (
        items.map((c) => (
          <div key={c.id} className="card-flat p-5" data-testid={`checkin-card-${c.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <div>
                <span className="font-semibold">{c.client_name || "Client"}</span>
                <span className="text-zinc-500 text-sm ml-2">week of {c.week_of}</span>
              </div>
              <span className={`tracker text-[10px] px-2 py-1 border ${c.status === "reviewed" ? "border-emerald-500/40 text-emerald-300" : "border-amber-500/40 text-amber-300"}`}>
                {c.status === "reviewed" ? "REVIEWED" : "AWAITING"}
              </span>
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-zinc-400 font-mono mb-2">
              <span>Energy {c.energy}/5</span><span>Sleep {c.sleep}/5</span><span>Stress {c.stress}/5</span>
              <span>Training {c.training}/5</span><span>Nutrition {c.nutrition}/5</span>
              {c.weight_kg && <span>{c.weight_kg}kg</span>}
            </div>
            {(c.wins || c.struggles) && (
              <div className="text-sm text-zinc-300 space-y-1 mb-3">
                {c.wins && <div><span className="text-emerald-400">Wins:</span> {c.wins}</div>}
                {c.struggles && <div><span className="text-[#FF6B61]">Struggles:</span> {c.struggles}</div>}
              </div>
            )}
            {(c.photos || []).length > 0 && (
              <div className="flex gap-2 mb-3">
                {c.photos.map((p, i) => (
                  <img key={i} src={p} alt="check-in" onClick={() => setLightbox(p)} className="w-16 h-16 object-cover border border-white/10 cursor-pointer" data-testid={`checkin-photo-${i}`} />
                ))}
              </div>
            )}
            {c.status === "reviewed" ? (
              <div className="p-3 bg-brand/10 border border-brand/30 text-sm" data-testid="reviewed-comment">
                <span className="tracker text-[9px] text-brand-light block mb-1">YOUR REPLY</span>{c.trainer_comment}
              </div>
            ) : (
              <div className="space-y-2">
                <textarea
                  value={comments[c.id] || ""}
                  onChange={(e) => setComments((s) => ({ ...s, [c.id]: e.target.value }))}
                  rows={comments[c.id] ? 3 : 1}
                  placeholder="Write feedback for the client…"
                  className="w-full px-3 py-2 border bg-[#0A0A0A] text-sm"
                  data-testid={`review-input-${c.id}`}
                />
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => draftWithAI(c.id)}
                    disabled={!!drafting[c.id]}
                    className="btn-ghost !py-2 !px-4 !text-xs disabled:opacity-40"
                    data-testid={`ai-draft-${c.id}`}
                  >
                    <Sparkle size={12} weight={drafting[c.id] ? "fill" : "bold"} className={drafting[c.id] ? "animate-pulse text-brand-light" : "text-brand-light"} />
                    {drafting[c.id] ? "Drafting…" : "Draft with AI"}
                  </button>
                  <button onClick={() => review(c.id)} className="btn-primary !py-2 !px-4 !text-xs" data-testid={`review-send-${c.id}`}>
                    <PaperPlaneRight size={12} weight="bold" /> Send
                  </button>
                </div>
              </div>
            )}
          </div>
        ))
      )}
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-6" onClick={() => setLightbox(null)} data-testid="photo-lightbox">
          <button className="absolute top-4 right-4 text-white"><X size={24} weight="bold" /></button>
          <img src={lightbox} alt="check-in full" className="max-h-full max-w-full object-contain" />
        </div>
      )}
    </div>
  );
}

function HabitsTab() {
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState("");
  const [habits, setHabits] = useState([]);
  const [adherence, setAdherence] = useState(null);
  const [name, setName] = useState("");
  const [target, setTarget] = useState(7);

  useEffect(() => {
    (async () => {
      try {
        const [cl, ad] = await Promise.all([api.get("/clients"), api.get("/habits/adherence", { params: { days: 7 } })]);
        setClients(cl.data || []);
        setAdherence(ad.data);
      } catch (_e) {}
    })();
  }, []);

  const loadHabits = async (cid) => {
    if (!cid) { setHabits([]); return; }
    try {
      const { data } = await api.get(`/clients/${cid}/habits`, { params: { days: 7, include_inactive: false } });
      setHabits(data);
    } catch (_e) { setHabits([]); }
  };
  useEffect(() => { loadHabits(selected); }, [selected]);

  const addHabit = async () => {
    if (!name.trim()) return;
    try {
      await api.post(`/clients/${selected}/habits`, { name: name.trim(), target_days_per_week: Number(target) });
      toast.success("Habit assigned");
      setName("");
      loadHabits(selected);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const removeHabit = async (h) => {
    try {
      await api.delete(`/habits/${h.id}`);
      toast.success("Habit removed");
      loadHabits(selected);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="habits-tab">
      {/* Adherence overview */}
      {adherence && adherence.rows.length > 0 && (
        <div className="card-flat" data-testid="adherence-panel">
          <div className="px-5 py-3 border-b border-white/10 tracker text-zinc-500">[ 7-DAY ADHERENCE — LOWEST FIRST ]</div>
          {adherence.rows.map((r) => (
            <div key={r.client_id} className="px-5 py-3 border-b border-white/5 last:border-b-0 flex items-center gap-4" data-testid={`adherence-row-${r.client_id}`}>
              <button onClick={() => setSelected(r.client_id)} className="flex-1 text-left text-sm hover:text-brand-light">{r.client_name}</button>
              <div className="text-xs text-zinc-500 font-mono">{r.checks}/{r.possible}</div>
              <div className="w-28 h-1.5 bg-white/10 relative">
                <div className={`absolute inset-y-0 left-0 ${r.adherence_pct >= 70 ? "bg-emerald-400" : r.adherence_pct >= 40 ? "bg-amber-400" : "bg-[#FF6B61]"}`} style={{ width: `${r.adherence_pct}%` }} />
              </div>
              <div className={`font-mono text-sm w-14 text-right ${r.adherence_pct >= 70 ? "text-emerald-400" : r.adherence_pct >= 40 ? "text-amber-400" : "text-[#FF6B61]"}`}>{r.adherence_pct}%</div>
            </div>
          ))}
        </div>
      )}

      <div className="card-flat p-5 space-y-4">
        <select value={selected} onChange={(e) => setSelected(e.target.value)} className="w-full sm:w-80 px-3 py-2 border bg-[#0A0A0A] text-sm" data-testid="habit-client-select">
          <option value="">— Pick a client to manage habits —</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {selected && (
          <div className="flex flex-col sm:flex-row gap-2">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder='e.g. "10k steps" or "Drink 2L water"' className="flex-1 px-3 py-2 border bg-[#0A0A0A] text-sm" data-testid="habit-name-input" />
            <select value={target} onChange={(e) => setTarget(e.target.value)} className="px-3 py-2 border bg-[#0A0A0A] text-sm" data-testid="habit-target-select">
              {[7, 6, 5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n}×/week</option>)}
            </select>
            <button onClick={addHabit} disabled={!name.trim()} className="btn-primary !py-2 !text-xs disabled:opacity-40" data-testid="habit-add-btn">
              <Plus size={12} weight="bold" /> Assign habit
            </button>
          </div>
        )}
      </div>

      {selected && <BadgeWall clientId={selected} compact />}

      {selected && (
        habits.length === 0 ? (
          <div className="card-flat p-8 text-center text-zinc-500 tracker" data-testid="trainer-habits-empty">No habits assigned yet</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {habits.map((h) => (
              <div key={h.id} className="relative">
                <HabitRow habit={h} readOnly onToggle={() => {}} />
                <button onClick={() => removeHabit(h)} className="absolute top-4 right-4 text-zinc-600 hover:text-[#FF6B61]" title="Remove habit" data-testid={`habit-delete-${h.id}`}>
                  <Trash size={16} weight="duotone" />
                </button>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}

function FormsTab() {
  const [rows, setRows] = useState(null);
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/forms/compliance");
        setRows(data.rows);
      } catch (_e) { setRows([]); }
    })();
  }, []);

  const openClient = async (r) => {
    try {
      const { data } = await api.get(`/clients/${r.client_id}/forms`);
      setViewing({ client: r, ...data });
    } catch (_e) { toast.error("Couldn't load forms"); }
  };

  const Pill = ({ done, flagged }) =>
    flagged ? (
      <span className="inline-flex items-center gap-1 text-amber-300 text-xs"><Warning size={13} weight="fill" /> Flagged</span>
    ) : done ? (
      <span className="inline-flex items-center gap-1 text-emerald-400 text-xs"><CheckCircle size={13} weight="fill" /> Done</span>
    ) : (
      <span className="inline-flex items-center gap-1 text-zinc-500 text-xs"><XCircle size={13} /> Missing</span>
    );

  return (
    <div data-testid="forms-tab">
      {rows === null ? (
        <div className="tracker text-zinc-500">Loading…</div>
      ) : (
        <div className="card-flat overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left tracker text-[10px] text-zinc-500 border-b border-white/10">
                <th className="px-5 py-3">CLIENT</th>
                <th className="px-5 py-3">PAR-Q</th>
                <th className="px-5 py-3">WAIVER</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.client_id} className="border-b border-white/5 last:border-b-0" data-testid={`compliance-row-${r.client_id}`}>
                  <td className="px-5 py-3">
                    <div className="font-semibold">{r.client_name}</div>
                    <div className="text-xs text-zinc-500 font-mono">{r.email}</div>
                  </td>
                  <td className="px-5 py-3"><Pill done={r.parq_done} flagged={r.parq_flagged} /></td>
                  <td className="px-5 py-3"><Pill done={r.waiver_done} /></td>
                  <td className="px-5 py-3 text-right">
                    {(r.parq_done || r.waiver_done) && (
                      <button onClick={() => openClient(r)} className="btn-ghost !py-1.5 !px-3 !text-xs" data-testid={`view-forms-${r.client_id}`}>View</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {viewing && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center overflow-y-auto p-4 sm:p-8" onClick={() => setViewing(null)} data-testid="forms-modal">
          <div className="card-flat bg-[#111] p-6 max-w-2xl w-full my-6 space-y-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div className="font-display text-2xl">{viewing.client.client_name} — Forms</div>
              <button onClick={() => setViewing(null)} className="text-zinc-500 hover:text-white" data-testid="forms-modal-close"><X size={20} weight="bold" /></button>
            </div>
            {viewing.submissions.filter((s) => s.type === "parq").map((s) => (
              <div key={s.id}>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="tracker text-zinc-500">[ PAR-Q · {new Date(s.created_at).toLocaleDateString()} · signed {s.signature_name} ]</div>
                  {viewing.parq_pdf_url && (
                    <a href={viewing.parq_pdf_url} target="_blank" rel="noreferrer" className="text-xs text-zinc-400 hover:text-white shrink-0" data-testid="modal-parq-pdf-link">Original PDF ↗</a>
                  )}
                </div>
                {(s.emergency_contact_name || s.emergency_contact_phone) && (
                  <div className="mb-2 p-2.5 bg-[#0A0A0A] border border-white/10 text-sm text-zinc-300" data-testid="modal-emergency-contact">
                    <span className="tracker text-[9px] text-zinc-500 block mb-1">EMERGENCY CONTACT</span>
                    {s.emergency_contact_name}{s.emergency_contact_phone && <span className="font-mono text-zinc-400"> · {s.emergency_contact_phone}</span>}
                  </div>
                )}
                <div className="space-y-1.5">
                  {viewing.parq_questions.map((q, i) => (
                    <div key={q.id} className="flex gap-3 text-sm">
                      <span className={`font-mono shrink-0 w-10 ${s.answers?.[q.id] ? "text-[#FF6B61]" : "text-emerald-400"}`}>{s.answers?.[q.id] ? "YES" : "NO"}</span>
                      <span className="text-zinc-400">{i + 1}. {q.text}</span>
                    </div>
                  ))}
                </div>
                {s.details && <div className="mt-2 p-3 bg-amber-500/10 border border-amber-500/30 text-sm text-amber-200">{s.details}</div>}
                {(s.activity_level || s.goals || s.injuries || s.limitations) && (
                  <div className="mt-3 text-sm text-zinc-300 space-y-1" data-testid="modal-activity-background">
                    <span className="tracker text-[9px] text-zinc-500 block">ACTIVITY BACKGROUND & GOALS</span>
                    {s.activity_level && <div><span className="text-zinc-500">Activity level:</span> {s.activity_level}</div>}
                    {s.goals && <div><span className="text-zinc-500">Goals:</span> {s.goals}</div>}
                    {s.injuries && <div><span className="text-zinc-500">Past injuries:</span> {s.injuries}</div>}
                    {s.limitations && <div><span className="text-zinc-500">Current limitations:</span> {s.limitations}</div>}
                  </div>
                )}
                {(s.favourite_movie || s.sports_played || s.favourite_sport_watch || s.hobbies) && (
                  <div className="mt-3 text-sm text-zinc-300 space-y-1" data-testid="modal-getting-to-know">
                    <span className="tracker text-[9px] text-zinc-500 block">GETTING TO KNOW THEM</span>
                    {s.favourite_movie && <div><span className="text-zinc-500">Favourite movie:</span> {s.favourite_movie}</div>}
                    {s.sports_played && <div><span className="text-zinc-500">Plays:</span> {s.sports_played}</div>}
                    {s.favourite_sport_watch && <div><span className="text-zinc-500">Watches:</span> {s.favourite_sport_watch}</div>}
                    {s.hobbies && <div><span className="text-zinc-500">Hobbies:</span> {s.hobbies}</div>}
                  </div>
                )}
              </div>
            ))}
            {viewing.submissions.filter((s) => s.type === "waiver").map((s) => (
              <div key={s.id}>
                <div className="tracker text-zinc-500 mb-2">[ WAIVER · {new Date(s.created_at).toLocaleDateString()} · signed {s.signature_name} ]</div>
                <div className="max-h-40 overflow-y-auto p-3 bg-[#0A0A0A] border border-white/10 text-xs text-zinc-500 whitespace-pre-wrap">{s.waiver_text}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
